import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./EmployerInformation.module.sass";
import Item from "../Item";
import TextInput from "../../../components/TextInput";
import Dropdown from "../../../components/Dropdown";
import { Link } from "react-router-dom";
import { searchCompanies } from "../../../services/company.service";
import { getTutorsByCompany } from "../../../services/tutor.service";

const ProfileInformation = ({ className, data, onSelectCompany, onSelectTutor }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [tutors, setTutors] = useState([]);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [selectedTutor2, setSelectedTutor2] = useState(null);
  const [loadingTutors, setLoadingTutors] = useState(false);

  // Effectuer la recherche lorsque la requête change
  useEffect(() => {
    const fetchCompanies = async () => {
      if (searchQuery.length >= 2) {
        try {
          const results = await searchCompanies(searchQuery);
          setSearchResults(results);
          setShowResults(true);
        } catch (error) {
          console.error('Erreur lors de la recherche des entreprises:', error);
          setSearchResults([]);
          setShowResults(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    };
    
    fetchCompanies();
  }, [searchQuery]);

  // Gérer la sélection d'une entreprise
  const handleSelectCompany = async (company) => {
    setSelectedCompany(company);
    setSearchQuery(company.name);
    setShowResults(false);
    setSelectedTutor(null); // Reset tuteur sélectionné
    setSelectedTutor2(null); // Reset second tuteur
    
    // Charger les tuteurs de l'entreprise
    await loadTutors(company);
    
    // Transmettre l'entreprise sélectionnée au composant parent si la prop existe
    if (onSelectCompany) {
      onSelectCompany(company);
    }
  };

  // Gérer le changement dans la barre de recherche
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    if (selectedCompany && e.target.value !== selectedCompany.name) {
      setSelectedCompany(null);
      setTutors([]);
      setSelectedTutor(null);
      setSelectedTutor2(null);
    }
  };

  // Charger les tuteurs d'une entreprise
  const loadTutors = async (company) => {
    if (!company) return;
    
    try {
      setLoadingTutors(true);
      console.log('Recherche tuteurs pour entreprise:', company);
      console.log('ID de l\'entreprise:', company.id);
      
      // Utiliser l'ID de l'entreprise pour rechercher les tuteurs
      const tutorsData = await getTutorsByCompany(company.id);
      console.log('Tuteurs trouvés:', tutorsData);
      
      // Transformer les données pour le dropdown
      const formattedTutors = tutorsData.map(tutor => ({
        ...tutor,
        value: tutor.id,
        label: `${tutor.first_name} ${tutor.last_name}${tutor.job_title ? ` - ${tutor.job_title}` : ''}`
      }));
      
      console.log('Tuteurs formatés pour dropdown:', formattedTutors);
      setTutors(formattedTutors);
    } catch (error) {
      console.error('Erreur lors du chargement des tuteurs:', error);
      setTutors([]);
    } finally {
      setLoadingTutors(false);
    }
  };

  // Gérer la sélection du premier tuteur
  const handleSelectTutor = (tutor) => {
    setSelectedTutor(tutor);
    
    // Si le second tuteur est le même que le premier, le réinitialiser
    if (selectedTutor2 && selectedTutor2.id === tutor.id) {
      setSelectedTutor2(null);
    }
    
    // Transmettre le tuteur sélectionné au composant parent si la prop existe
    if (onSelectTutor) {
      onSelectTutor(tutor);
    }
  };

  // Gérer la sélection du second tuteur
  const handleSelectTutor2 = (tutor) => {
    setSelectedTutor2(tutor);
    
    // Transmettre le second tuteur au composant parent si la prop existe
    if (onSelectTutor) {
      onSelectTutor(tutor, true); // true indique que c'est le second tuteur
    }
  };

  // Filtrer les tuteurs pour le second dropdown (exclure le premier tuteur sélectionné)
  const getAvailableTutorsForSecond = () => {
    if (!selectedTutor) return tutors;
    return tutors.filter(tutor => tutor.id !== selectedTutor.id);
  };

  // Formater l'affichage des tuteurs dans le dropdown
  const formatTutorDisplay = (tutor) => {
    if (!tutor) return "Sélectionner un tuteur";
    return tutor.label || `${tutor.first_name} ${tutor.last_name}${tutor.job_title ? ` - ${tutor.job_title}` : ''}`;
  };

  return (
    <Item
      className={cn(styles.card, className)}
      title="Entreprise"
      classTitle="title-green"
    >
      <div className={styles.fieldset}>
        <div className={styles.searchContainer}>
          <TextInput
            className={styles.field}
            label="Rechercher une entreprise"
            name="company-search"
            type="text"
            placeholder="Nom de l'entreprise ou SIRET (min. 3 caractères)"
            tooltip="Recherchez par nom d'entreprise ou numéro SIRET"
            value={searchQuery}
            onChange={handleSearchChange}
            required
          />
          
          {showResults && searchResults.length > 0 && (
            <div className={styles.searchResults}>
              {searchResults.map(company => (
                <div 
                  key={company.id} 
                  className={styles.resultItem}
                  onClick={() => handleSelectCompany(company)}
                >
                  <div className={styles.resultContent}>
                    <img 
                      src={company.logoUrl || '/images/content/company_logo.png'} 
                      alt={company.name} 
                      className={styles.companyLogo} 
                    />
                    <div className={styles.companyInfo}>
                      <div className={styles.companyName}>{company.name}</div>
                      <div className={styles.companyDetails}>
                        <span>{company.sector}</span>
                        <span>•</span>
                        <span>SIRET: {company.siret}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showResults && searchResults.length === 0 && searchQuery.length >= 2 && (
            <div className={styles.noResults}>
              Aucune entreprise trouvée
              <Link
                className={styles.link}
                to="/admin/companies/create"
              >
                Ajouter une entreprise
              </Link>
            </div>
          )}
        </div>

        {selectedCompany && (
          <div className={styles.selectedCompanyDetails}>
            <h3>Détails de l'entreprise sélectionnée</h3>
            <div className={styles.detailRow}>
              <span className={styles.label}>Nom:</span>
              <span className={styles.value}>{selectedCompany.name}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>SIRET:</span>
              <span className={styles.value}>{selectedCompany.siret || 'Non renseigné'}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Secteur:</span>
              <span className={styles.value}>{selectedCompany.sector || 'Non renseigné'}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Adresse:</span>
              <span className={styles.value}>
                {selectedCompany.address ? 
                  `${selectedCompany.address}, ${selectedCompany.zipCode || ''} ${selectedCompany.city || ''}` : 
                  'Non renseignée'}
              </span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Contact:</span>
              <span className={styles.value}>
                {selectedCompany.contactFirstName ? 
                  `${selectedCompany.contactFirstName} ${selectedCompany.contactLastName || ''} ${selectedCompany.contactJob ? `(${selectedCompany.contactJob})` : ''}` : 
                  'Aucun contact renseigné'}
              </span>
            </div>
          </div>
        )}

        {/* Dropdown de sélection des tuteurs */}
        {selectedCompany && (
          <div className={styles.tutorSelection}>
            <Dropdown
              className={styles.tutorDropdown}
              label="Maître d'apprentissage"
              tooltip="Sélectionnez le tuteur qui encadrera l'apprenti dans cette entreprise"
              value={selectedTutor}
              setValue={handleSelectTutor}
              options={tutors}
              displayFormatter={formatTutorDisplay}
              placeholder={loadingTutors ? "Chargement des tuteurs..." : tutors.length === 0 ? "Aucun tuteur disponible" : "Sélectionner un tuteur"}
              required
            />
            
            {/* Second dropdown pour le deuxième tuteur */}
            {tutors.length > 1 && (
              <Dropdown
                className={styles.tutorDropdown}
                label="Second maître d'apprentissage (optionnel)"
                tooltip="Sélectionnez un second tuteur pour ce contrat (optionnel)"
                value={selectedTutor2}
                setValue={handleSelectTutor2}
                options={getAvailableTutorsForSecond()}
                displayFormatter={formatTutorDisplay}
                placeholder="Sélectionner un second tuteur (optionnel)"
              />
            )}
            
            {tutors.length === 0 && !loadingTutors && (
              <div className={styles.noTutors}>
                <p>Aucun tuteur trouvé pour cette entreprise.</p>
                <Link
                  className={styles.link}
                  to={`/company/${selectedCompany.id}#tutors`}
                  target="_blank"
                >
                  Ajouter un tuteur à cette entreprise
                </Link>
              </div>
            )}
          </div>
        )}

        {selectedTutor && (
          <div className={styles.selectedTutorDetails}>
            <h3>Détails du tuteur principal</h3>
            <div className={styles.detailRow}>
              <span className={styles.label}>Nom complet:</span>
              <span className={styles.value}>{selectedTutor.first_name} {selectedTutor.last_name}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Fonction:</span>
              <span className={styles.value}>{selectedTutor.job_title || 'Non renseignée'}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Email:</span>
              <span className={styles.value}>{selectedTutor.email || 'Non renseigné'}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Téléphone:</span>
              <span className={styles.value}>{selectedTutor.phone || 'Non renseigné'}</span>
            </div>
          </div>
        )}

        {selectedTutor2 && (
          <div className={styles.selectedTutorDetails}>
            <h3>Détails du second tuteur</h3>
            <div className={styles.detailRow}>
              <span className={styles.label}>Nom complet:</span>
              <span className={styles.value}>{selectedTutor2.first_name} {selectedTutor2.last_name}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Fonction:</span>
              <span className={styles.value}>{selectedTutor2.job_title || 'Non renseignée'}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Email:</span>
              <span className={styles.value}>{selectedTutor2.email || 'Non renseigné'}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Téléphone:</span>
              <span className={styles.value}>{selectedTutor2.phone || 'Non renseigné'}</span>
            </div>
          </div>
        )}
      </div>
    </Item>
  );
};

export default ProfileInformation;